

import { useState, useEffect } from "react"
import { useHistory, useParams, Link } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import Header from "../../../components/admin/common/Header"
import SpinnerLoader from "../../../components/common/SpinnerLoader"

const DocumentVersions = () => {
  const [loading, setLoading] = useState(true)
  const [document, setDocument] = useState(null)
  const [versions, setVersions] = useState([])
  const [selectedVersions, setSelectedVersions] = useState([])
  const [showCompareModal, setShowCompareModal] = useState(false)

  const history = useHistory()
  const { documentId } = useParams()
  const authInfo = useSelector((state) => state.auth.authInfo)

  useEffect(() => {
    fetchDocumentAndVersions()
  }, [documentId])

  const fetchDocumentAndVersions = async () => {
    try {
      setLoading(true)

      // Fetch current document
      const docResponse = await axios.get(`admin/legaldocument/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (docResponse.data.status) {
        setDocument(docResponse.data.data)
      }

      // Fetch document versions using the actual endpoint
      const versionsResponse = await axios.get(`admin/legaldocument/documents/${documentId}/versions`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (versionsResponse.data.status) {
        const versionsData = versionsResponse.data.data.map((version, index) => ({
          ...version,
          isCurrent: index === 0, // First version (highest version number) is current
          changeLog: version.changeLog || version.versionNotes || `Version ${version.version}`,
          wordCount: version.wordCount || (version.content ? version.content.split(" ").length : 0),
          status: version.isActive ? "published" : "superseded",
        }))
        setVersions(versionsData)
      }
    } catch (error) {
      console.error("Error fetching document versions:", error)
      toast.error("Failed to fetch document versions")
      history.push("/admin/legal-documents")
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getVersionBadgeClass = (versionType) => {
    const classes = {
      major: "bg-danger",
      minor: "bg-warning",
      patch: "bg-info",
    }
    return classes[versionType] || "bg-secondary"
  }

  const getStatusBadgeClass = (status) => {
    const classes = {
      published: "bg-success",
      draft: "bg-secondary",
      superseded: "bg-warning",
      archived: "bg-dark",
    }
    return classes[status] || "bg-secondary"
  }

  const handleVersionSelect = (versionId) => {
    setSelectedVersions((prev) => {
      if (prev.includes(versionId)) {
        return prev.filter((id) => id !== versionId)
      } else if (prev.length < 2) {
        return [...prev, versionId]
      } else {
        // Replace the first selected version
        return [prev[1], versionId]
      }
    })
  }

  const handleCompareVersions = () => {
    if (selectedVersions.length === 2) {
      setShowCompareModal(true)
    }
  }

  const handleRestoreVersion = async (versionId) => {
    if (
      !window.confirm(
        "Are you sure you want to restore this version? This will create a new version based on the selected one.",
      )
    ) {
      return
    }

    try {
      const response = await axios.post(
        `admin/legaldocument/documents/${documentId}/restore/${versionId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authInfo.token}`,
          },
        },
      )

      if (response.data.status) {
        toast.success("Version restored successfully!")
        fetchDocumentAndVersions() // Refresh the versions list
      }
    } catch (error) {
      console.error("Error restoring version:", error)
      toast.error(error.response?.data?.message || "Failed to restore version")
    }
  }

  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm("Are you sure you want to delete this version? This action cannot be undone.")) {
      return
    }

    try {
      const response = await axios.delete(`admin/legaldocument/documents/versions/${versionId}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        toast.success("Version deleted successfully!")
        fetchDocumentAndVersions() // Refresh the versions list
      }
    } catch (error) {
      console.error("Error deleting version:", error)
      toast.error(error.response?.data?.message || "Failed to delete version")
    }
  }

  if (loading) {
    return (
      <>
        <Header />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
          <SpinnerLoader />
        </div>
      </>
    )
  }

  if (!document) {
    return (
      <>
        <Header />
        <div className="container-fluid" >
          <div className="text-center py-5">
            <h4>Document not found</h4>
            <button className="btn btn-primary" onClick={() => history.push("/admin/legal-documents")}>
              Back to Documents
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Header />
      <div className="document-versions" style={{ minHeight: "100vh", overflowY: "auto", padding: "18px 100px" }}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="mb-1">Document Versions</h2>
                  <p className="text-muted">Version history for: {document.title}</p>
                </div>
                <div>
                  {selectedVersions.length === 2 && (
                    <button className="btn btn-info me-2" onClick={handleCompareVersions}>
                      Compare Selected
                    </button>
                  )}
                  <Link to={`/admin/legal-documents/view/${documentId}`} className="btn btn-outline-primary me-2">
                    View Current
                  </Link>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => history.push("/admin/legal-documents")}
                  >
                    Back to Documents
                  </button>
                </div>
              </div>

              {/* Version Selection Info */}
              {selectedVersions.length > 0 && (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  {selectedVersions.length === 1
                    ? "Select one more version to compare"
                    : `Selected ${selectedVersions.length} versions for comparison`}
                  {selectedVersions.length > 0 && (
                    <button className="btn btn-sm btn-outline-secondary ms-2" onClick={() => setSelectedVersions([])}>
                      Clear Selection
                    </button>
                  )}
                </div>
              )}

              {/* Versions List */}
              <div className="row">
                {versions.map((version) => (
                  <div key={version._id} className="col-lg-6 mb-4">
                    <div className={`card h-100 ${selectedVersions.includes(version._id) ? "border-primary" : ""}`}>
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <div className="form-check me-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`version-${version._id}`}
                              checked={selectedVersions.includes(version._id)}
                              onChange={() => handleVersionSelect(version._id)}
                              disabled={!selectedVersions.includes(version._id) && selectedVersions.length >= 2}
                            />
                          </div>
                          <div>
                            <h6 className="mb-0">
                              Version {version.version}
                              {version.isCurrent && <span className="badge bg-success ms-2">Current</span>}
                            </h6>
                            <small className="text-muted">
                              <span className={`badge ${getVersionBadgeClass(version.versionType)} me-2`}>
                                {version.versionType}
                              </span>
                              <span className={`badge ${getStatusBadgeClass(version.status)}`}>{version.status}</span>
                            </small>
                          </div>
                        </div>
                        {/* <div className="dropdown">
                          <button
                            className="btn btn-sm btn-outline-secondary dropdown-toggle"
                            type="button"
                            data-bs-toggle="dropdown"
                          >
                            <i className="fas fa-ellipsis-v"></i>
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            <li>
                              <Link className="dropdown-item" to={`/admin/legal-documents/view/${version._id}`}>
                                View
                              </Link>
                            </li>
                            {!version.isCurrent && (
                              <>
                                <li>
                                  <button className="dropdown-item" onClick={() => handleRestoreVersion(version._id)}>
                                    Restore
                                  </button>
                                </li>
                                <li>
                                  <hr className="dropdown-divider" />
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item text-danger"
                                    onClick={() => handleDeleteVersion(version._id)}
                                  >
                                    Delete
                                  </button>
                                </li>
                              </>
                            )}
                          </ul>
                        </div> */}
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <strong>Change Log:</strong>
                          <p className="mb-2">{version.changeLog}</p>
                        </div>

                        <div className="row text-center">
                          <div className="col-4">
                            <div className="border-end">
                              <div className="fw-bold">{version.wordCount}</div>
                              <small className="text-muted">Words</small>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="border-end">
                              <div className="fw-bold">{formatDate(version.createdAt).split(",")[0]}</div>
                              <small className="text-muted">Date</small>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="fw-bold">{version.createdBy?.name}</div>
                            <small className="text-muted">Author</small>
                          </div>
                        </div>

                        <hr />

                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">Created: {formatDate(version.createdAt)}</small>
                          {version.isCurrent && (
                            <Link to={`/admin/legal-documents/edit/${version._id}`} className="btn btn-sm btn-primary">
                              Edit
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {versions.length === 0 && (
                <div className="text-center py-5">
                  <i className="fas fa-history fa-3x text-muted mb-3"></i>
                  <h4>No Versions Found</h4>
                  <p className="text-muted">No version history available for this document</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Compare Versions Modal */}
      {showCompareModal && selectedVersions.length === 2 && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Compare Versions</h5>
                <button type="button" className="btn-close" onClick={() => setShowCompareModal(false)}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                <div className="row">
                  {selectedVersions.map((versionId, index) => {
                    const version = versions.find((v) => v._id === versionId)
                    return (
                      <div key={versionId} className="col-md-6">
                        <div className="card">
                          <div className="card-header">
                            <h6 className="mb-0">
                              Version {version?.version}
                              <span className={`badge ${getVersionBadgeClass(version?.versionType)} ms-2`}>
                                {version?.versionType}
                              </span>
                            </h6>
                            <small className="text-muted">
                              {formatDate(version?.createdAt)} by {version?.createdBy?.name}
                            </small>
                          </div>
                          <div className="card-body" style={{ maxHeight: "400px", overflowY: "auto" }}>
                            <div className="mb-3">
                              <strong>Change Log:</strong>
                              <p>{version?.changeLog}</p>
                            </div>
                            <div className="mb-3">
                              <strong>Word Count:</strong> {version?.wordCount}
                            </div>
                            <div className="mb-3">
                              <strong>Status:</strong>
                              <span className={`badge ${getStatusBadgeClass(version?.status)} ms-2`}>
                                {version?.status}
                              </span>
                            </div>
                            <div>
                              <strong>Content Preview:</strong>
                              <div
                                className="border p-2 mt-2 bg-light"
                                style={{ maxHeight: "200px", overflowY: "auto" }}
                                dangerouslySetInnerHTML={{
                                  __html: version?.content?.substring(0, 500) + "..." || "No content available",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCompareModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DocumentVersions